import React from "react";
import { Box, Typography } from "@mui/material";
import Spinner from "../Spinner";

/**
 * Shared page shell for authenticated admin screens.
 *
 * Props:
 * - title: page heading
 * - subtitle: optional secondary text under the title
 * - actions: right-side header actions (buttons, etc.)
 * - search: optional search/filter row below the header
 * - loading: show full-page spinner overlay
 * - children: page content
 * - noPadding: skip outer content padding (rare)
 * - maxWidth: optional max width for content (default none)
 */
const PageLayout = ({
  title,
  subtitle,
  actions,
  search,
  loading = false,
  children,
  noPadding = false,
  sx = {},
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...sx,
      }}
    >
      {loading && <Spinner />}

      {(title || actions) && (
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: { xs: 1.5, sm: 2 },
            mb: search ? 1.5 : { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
            {title && (
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#142C5C",
                  fontSize: { xs: "18px", sm: "20px", md: "24px" },
                  lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                  fontSize: { xs: "12px", md: "13px" },
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {actions && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                flexShrink: 0,
                // On phones the actions drop below the title and span the
                // full width so buttons don't get squeezed next to it.
                width: { xs: "100%", sm: "auto" },
                minWidth: 0,
              }}
            >
              {actions}
            </Box>
          )}
        </Box>
      )}

      {search && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2.5,
            minWidth: 0,
          }}
        >
          {search}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          width: "100%",
          boxSizing: "border-box",
          padding: noPadding ? 0 : 0,
          minHeight: 0,
          // Let wide children (tables, charts) scroll inside their own
          // containers instead of stretching the page on narrow screens.
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default PageLayout;
